import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Users, Clock, CheckCircle, XCircle,
  AlertCircle, Play, Eye, ChevronLeft, Search, TrendingUp, BookOpen,
  Building, Settings,
  Award
} from 'lucide-react';
import { courseAssignmentAnalyticsService } from '@/services/courseAssignmentAnalytics.service';
import { userService } from '@/services/user.service';
import { groupRoleService, type Role } from '@/services/groupRole.service';
import type { UserAnalytics, GroupAnalytics } from '@/types/courseAssignmentAnalytics.types';
import { toast } from '@/components/ui/Toast';
import {Select} from '@/components/ui/Select';

interface GroupInfo {
  _id: string;
  group_key: string;
  group_name: string;
  description?: string;
  parent_group_key?: string;
  created_at?: string;
  updated_at?: string;
}

interface FilterOptions {
  courseStatus: string;
  certificationStatus: string;
  sortBy: string;
}

export default function GroupCourseList() {
  const { groupKey } = useParams<{ groupKey: string }>();
  const navigate = useNavigate();
   const location = useLocation();
  const [users, setUsers] = useState<UserAnalytics[]>([]);
  const [groupInfo, setGroupInfo] = useState<GroupInfo | null>(null);
  const [groupAnalytics, setGroupAnalytics] = useState<GroupAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [courseStatus, setCourseStatus] = useState<string>('all');
  const [certificationStatus, setCertificationStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('progress');
  const [showRoleModal, setShowRoleModal] = useState(false);

  // Form setup for Select components
  const { control, watch } = useForm<FilterOptions>({
    defaultValues: {
      courseStatus: 'all',
      certificationStatus: 'all',
      sortBy: 'progress'
    }
  });

  // Watch form changes and sync with state
  useEffect(() => {
    const subscription = watch((value) => {
      if (value.courseStatus !== undefined) {
        setCourseStatus(value.courseStatus);
      }
      if (value.certificationStatus !== undefined) {
        setCertificationStatus(value.certificationStatus);
      }
      if (value.sortBy !== undefined) {
        setSortBy(value.sortBy);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    fetchData();
  }, [groupKey]);

  // Handle dynamic back navigation
  const handleBackNavigation = () => {
      const returnPath = location.state?.from || '/admin/analytics/department-progress';
      navigate(returnPath);
      
  };

  const fetchData = async () => {
    if (!groupKey) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch group info
      const groupResponse = await userService.getGroupByKey(groupKey);
      if (groupResponse?.data) {
        setGroupInfo(groupResponse.data);
      }

      // Fetch group analytics with group_key filter
      const analyticsResponse = await courseAssignmentAnalyticsService.getGroupAnalytics(1, 20, '', '', '', groupKey);
      const groupData = analyticsResponse.groups.find(g => g.groupKey === groupKey);
      if (groupData) {
        setGroupAnalytics(groupData);
      }

      // Fetch all users in this group with their course progress (no pagination)
      const userAnalyticsResponse = await courseAssignmentAnalyticsService.getAllUserAnalytics({
        groupKey: groupKey
      });
      
      if (userAnalyticsResponse?.users) {
        setUsers(userAnalyticsResponse.users);
      }
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError('Failed to load group course data');
      toast.error('Failed to load group course data');
    } finally {
      setLoading(false);
    }
  };

  // Select options
  const courseStatusOptions = [
    { label: 'All Course Status', value: 'all' },
    { label: 'Not Started', value: 'not_started' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Completed', value: 'completed' },
  ];

  const certificationStatusOptions = [
    { label: 'All Cert Status', value: 'all' },
    { label: 'Not Started', value: 'not_started' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Completed', value: 'completed' },
  ];

  const sortOptions = [
    { label: 'Progress', value: 'progress' },
    { label: 'Course Time Spent', value: 'timeSpent' },
    { label: 'Certification Time Spent', value: 'certificationTimeSpent' },
    { label: 'Certification Count', value: 'certificationCount' },
    { label: 'Completed Courses', value: 'completedCourseCount' },
    { label: 'Last Active', value: 'lastActive' },
    { label: 'Name', value: 'name' }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'in_progress':
        return 'text-blue-600 bg-blue-100';
      case 'not_started':
        return 'text-gray-600 bg-gray-100';
      case 'overdue':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'in_progress':
        return <Play className="w-4 h-4" />;
      case 'not_started':
        return <AlertCircle className="w-4 h-4" />;
      case 'overdue':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatTimeFromSeconds = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getEngagementColor = (engagement: string) => {
    switch (engagement) {
      case 'high':
        return 'text-green-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  // Helper function to format date, handling 'na' and invalid dates
  const formatDate = (dateValue: any): string => {
    if (!dateValue || dateValue === 'na' || dateValue === 'N/A') {
      return '-';
    }
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return '-';
      }
      return date.toLocaleDateString();
    } catch (error) {
      return '-';
    }
  };

  // Helper function to calculate user status based on course_stats
  const getUserStatus = (user: UserAnalytics): string => {
    if (!user.course_stats) {
      return user.status || 'not_started';
    }

    const { completed, in_progress, not_started } = user.course_stats;
    const totalCourses = completed + in_progress + not_started;

    // If no courses assigned
    if (totalCourses === 0) {
      return 'not_started';
    }

    // If all courses are completed
    if (completed === totalCourses) {
      return 'completed';
    }

    // If all courses are not started
    if (not_started === totalCourses) {
      return 'not_started';
    }

    // Otherwise, it's in progress (some courses started but not all completed)
    return 'in_progress';
  };

  // Helper function to calculate certification status based on certification_stats
  const getCertificationStatus = (user: UserAnalytics): string => {
    if (!user.certification_stats) {
      return 'not_started';
    }

    const { completed, in_progress, not_started } = user.certification_stats;
    const totalCertifications = completed + in_progress + not_started;

    // If no certifications assigned
    if (totalCertifications === 0) {
      return 'not_started';
    }

    // If all certifications are completed
    if (completed === totalCertifications) {
      return 'completed';
    }

    // If all certifications are not started
    if (not_started === totalCertifications) {
      return 'not_started';
    }

    // Otherwise, it's in progress (some certifications started but not all completed)
    return 'in_progress';
  };

  const filteredUsers = users.filter(user => {
    // Apply course status filter
    if (courseStatus !== 'all' && getUserStatus(user) !== courseStatus) {
      return false;
    }

    // Apply certification status filter
    if (certificationStatus !== 'all' && getCertificationStatus(user) !== certificationStatus) {
      return false;
    }

    // Apply search filter
    if (searchQuery &&
        !user.userName.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !user.email.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    return true;
  });

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    switch (sortBy) {
      case 'progress':
        return b.progress - a.progress;
      case 'timeSpent':
        return b.timeSpent - a.timeSpent;
      case 'certificationTimeSpent':
        return b.certification_time_spent - a.certification_time_spent;
      case 'certificationCount':
        return b.certification_count - a.certification_count;
      case 'completedCourseCount':
        return b.completedCourseCount - a.completedCourseCount;
      case 'lastActive':
        return new Date(b.lastActive || 0).getTime() - new Date(a.lastActive || 0).getTime();
      case 'name':
        return a.userName.localeCompare(b.userName);
      default:
        return 0;
    }
  });

  // Calculate statistics
  const stats = {
    total: users.length,
    // Course stats - total count across all users
    courseCompleted: users.reduce((sum, u) => sum + (u.course_stats?.completed || 0), 0),
    courseInProgress: users.reduce((sum, u) => sum + (u.course_stats?.in_progress || 0), 0),
    courseNotStarted: users.reduce((sum, u) => sum + (u.course_stats?.not_started || 0), 0),
    averageCourseProgress: users.reduce((sum, u) => sum + u.progress, 0) / (users.length || 1),
    // Certification stats - total count across all users
    certCompleted: users.reduce((sum, u) => sum + (u.certification_stats?.completed || 0), 0),
    certInProgress: users.reduce((sum, u) => sum + (u.certification_stats?.in_progress || 0), 0),
    certNotStarted: users.reduce((sum, u) => sum + (u.certification_stats?.not_started || 0), 0),
    averageCertProgress: users.length > 0 ? users.reduce((sum, u) => {
      const totalCerts = u.certification_count || 0;
      const completedCerts = u.certification_stats?.completed || 0;
      return sum + (totalCerts > 0 ? (completedCerts / totalCerts) * 100 : 0);
    }, 0) / users.length : 0,
    // Time stats
    totalTime: users.reduce((sum, u) => sum + u.timeSpent, 0),
    totalCertificationTime: users.reduce((sum, u) => sum + u.certification_time_spent, 0),
    highEngagement: users.filter(u => u.engagement === 'high').length
  };

  const handleViewUserCourses = (userId: string) => {
    // Navigate to user's course list
    navigate(
      `/admin/users/${userId}/courses`,
      { state: { from: `/admin/groups/${groupKey}/courses` } }
    );
  };

  const handleViewUserCertifications = (userId: string) => {
    // Navigate to user's certification list
    navigate(
      `/admin/users/${userId}/certifications`,
      { state: { from: `/admin/groups/${groupKey}/courses` } }
    );
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
            <div>
              <div className="h-8 w-64 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-48 bg-gray-200 rounded"></div>
            </div>
          </div>
          <div className="h-10 w-40 bg-gray-200 rounded-lg"></div>
        </div>

        {/* Group Info Card Skeleton */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
            <div className="flex-1">
              <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-96 bg-gray-200 rounded mb-3"></div>
              <div className="flex items-center gap-4">
                <div className="h-4 w-24 bg-gray-200 rounded"></div>
                <div className="h-4 w-24 bg-gray-200 rounded"></div>
                <div className="h-4 w-32 bg-gray-200 rounded"></div>
                <div className="h-6 w-32 bg-gray-200 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className="w-8 h-8 bg-gray-200 rounded"></div>
                <div className="h-8 w-16 bg-gray-200 rounded"></div>
              </div>
              <div className="h-4 w-24 bg-gray-200 rounded mb-3"></div>
              <div className="h-3 w-32 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>

        {/* Filters Skeleton */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[300px] h-10 bg-gray-200 rounded-lg"></div>
            <div className="w-48 h-10 bg-gray-200 rounded-lg"></div>
            <div className="w-48 h-10 bg-gray-200 rounded-lg"></div>
            <div className="w-48 h-10 bg-gray-200 rounded-lg"></div>
          </div>
        </div>

        {/* Legend Skeleton */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div className="h-4 w-32 bg-gray-200 rounded"></div>
            <div className="flex items-center gap-6">
              <div className="h-4 w-24 bg-gray-200 rounded"></div>
              <div className="h-4 w-24 bg-gray-200 rounded"></div>
              <div className="h-4 w-24 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>

        {/* Table Skeleton */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => (
                    <th key={i} className="px-6 py-3">
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[1, 2, 3, 4, 5].map((row) => (
                  <tr key={row}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((col) => (
                      <td key={col} className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-gray-900 font-medium mb-1">Failed to Load</p>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackNavigation}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Go back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Department Overview</h1>
            <p className="text-gray-600 mt-1">
              Assignment data of {groupInfo?.group_name || groupKey}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowRoleModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Settings className="w-4 h-4" />
            Role Management
          </button>
          {/* <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Download className="w-4 h-4" />
            Export
          </button> */}
        </div>
      </div>

      {/* Group Info Card */}
      {groupInfo && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-brand-blue-400 to-brand-blue-600 rounded-full flex items-center justify-center text-white">
              <Building className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">
                {groupInfo.group_name}
              </h2>
              {groupInfo.description && (
                <p className="text-gray-600">{groupInfo.description}</p>
              )}
              <div className="flex items-center gap-4 mt-2 text-sm">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4 text-gray-400" />
                  {stats.total || 0} Members
                </span>
                <span className="flex items-center gap-1">
                  <BookOpen className="w-4 h-4 text-gray-400" />
                  {groupAnalytics?.assignedCourses || 0} Courses
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-gray-400" />
                  {groupAnalytics?.certification_count || 0} Certifications
                </span>
                {groupAnalytics?.engagementLevel && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    groupAnalytics.engagementLevel === 'high' ? 'bg-green-100 text-green-700' :
                    groupAnalytics.engagementLevel === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {groupAnalytics.engagementLevel} engagement
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Users */}
        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-8 h-8 text-brand-blue-600" />
            <span className="text-2xl font-bold text-gray-900">{stats.total}</span>
          </div>
          <p className="text-gray-600 text-sm">Total Users</p>
          <div className="mt-3 text-xs text-gray-500">
            {stats.highEngagement} high engagement
          </div>
        </div>

        {/* Average Course Progress */}
        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <BookOpen className="w-8 h-8 text-brand-blue-600" />
            <span className="text-2xl font-bold text-gray-900">
              {Math.round(stats.averageCourseProgress)}%
            </span>
          </div>
          <p className="text-gray-600 text-sm">Avg Course Progress</p>
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-brand-blue-600 h-2 rounded-full"
                style={{ width: `${stats.averageCourseProgress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Average Cert Progress */}
        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Award className="w-8 h-8 text-brand-purple-600" />
            <span className="text-2xl font-bold text-gray-900">
              {Math.round(stats.averageCertProgress)}%
            </span>
          </div>
          <p className="text-gray-600 text-sm">Avg Cert Progress</p>
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-brand-purple-600 h-2 rounded-full"
                style={{ width: `${stats.averageCertProgress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Total Time Invested */}
        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-8 h-8 text-brand-orange-600" />
            <span className="text-2xl font-bold text-gray-900">
              {formatTimeFromSeconds(stats.totalTime + stats.totalCertificationTime)}
            </span>
          </div>
          <p className="text-gray-600 text-sm">Total Time</p>
          <div className="mt-3 text-xs text-gray-500">
            <div>Course: {formatTimeFromSeconds(stats.totalTime)}</div>
            <div>Cert: {formatTimeFromSeconds(stats.totalCertificationTime)}</div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="w-48">
            <Select
              name="courseStatus"
              control={control}
              options={courseStatusOptions}
              isSearchable={false}
              placeholder="Course status"
            />
          </div>

          <div className="w-48">
            <Select
              name="certificationStatus"
              control={control}
              options={certificationStatusOptions}
              isSearchable={false}
              placeholder="Cert status"
            />
          </div>

          <div className="w-48">
            <Select
              name="sortBy"
              control={control}
              options={sortOptions}
              isSearchable={false}
              placeholder="Sort by"
            />
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Status Legend</h3>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm text-gray-600"><span className="font-medium text-green-600">C</span> - Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-sm text-gray-600"><span className="font-medium text-blue-600">P</span> - In Progress</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-500"></div>
              <span className="text-sm text-gray-600"><span className="font-medium text-gray-600">N</span> - Not Started</span>
            </div>
          </div>
        </div>
      </div>

      {/* User List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Course Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Courses
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time Spent (Course)
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cert Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Certifications
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time Spent (Cert)
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Engagement
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Active
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedUsers.map((user) => (
                <tr key={user.userId} className="hover:bg-gray-50">
                  {/* User */}
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {user.userName}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {user.email}
                      </p>
                    </div>
                  </td>

                  {/* Course Status */}
                  <td className="px-6 py-4">
                    {user.course_stats ? (
                      <div className="flex items-center justify-center">
                        <div className="flex flex-col text-center">
                          <div className="flex gap-1 text-xs">
                            <span className="text-green-600 font-medium">{user.course_stats.completed}</span>
                            <span className="text-gray-400">/</span>
                            <span className="text-blue-600 font-medium">{user.course_stats.in_progress}</span>
                            <span className="text-gray-400">/</span>
                            <span className="text-gray-600 font-medium">{user.course_stats.not_started}</span>
                          </div>
                          <span className="text-xs text-gray-500">C/P/N</span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>

                  {/* Progress */}
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-3">
                      <div className="flex-1 max-w-[100px]">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              user.progress >= 80 ? 'bg-green-600' :
                              user.progress >= 50 ? 'bg-blue-600' :
                              user.progress >= 20 ? 'bg-yellow-600' :
                              'bg-red-600'
                            }`}
                            style={{ width: `${user.progress}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {user.progress}%
                      </span>
                    </div>
                  </td>

                  {/* Courses */}
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    {user.assignedCourses}
                  </td>

                  {/* Time Spent (Course) */}
                  <td className="px-6 py-4 text-center text-sm text-gray-600">
                    {formatTimeFromSeconds(user.timeSpent)}
                  </td>

                  {/* Cert Status */}
                  <td className="px-6 py-4">
                    {user.certification_stats ? (
                      <div className="flex items-center justify-center">
                        <div className="flex flex-col text-center">
                          <div className="flex gap-1 text-xs">
                            <span className="text-green-600 font-medium">{user.certification_stats.completed}</span>
                            <span className="text-gray-400">/</span>
                            <span className="text-blue-600 font-medium">{user.certification_stats.in_progress}</span>
                            <span className="text-gray-400">/</span>
                            <span className="text-gray-600 font-medium">{user.certification_stats.not_started}</span>
                          </div>
                          <span className="text-xs text-gray-500">C/P/N</span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>

                  {/* Certifications */}
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    {user.certification_count}
                  </td>

                  {/* Time Spent (Cert) */}
                  <td className="px-6 py-4 text-center text-sm text-gray-600">
                    {formatTimeFromSeconds(user.certification_time_spent)}
                  </td>

                  {/* Engagement */}
                  <td className="px-6 py-4 text-center">
                    <span className={`text-sm font-medium ${getEngagementColor(user.engagement)}`}>
                      {user.engagement}
                    </span>
                  </td>

                  {/* Last Active */}
                  <td className="px-6 py-4 text-center text-sm text-gray-600">
                    {formatDate(user.lastActive)}
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleViewUserCourses(user.userId)}
                        className="p-2 text-brand-blue-600 hover:bg-brand-blue-50 rounded-lg transition-colors"
                        title="View Courses"
                      >
                        <BookOpen className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleViewUserCertifications(user.userId)}
                        className="p-2 text-brand-purple-600 hover:bg-brand-purple-50 rounded-lg transition-colors"
                        title="View Certifications"
                      >
                        <Award className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {sortedUsers.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No Tracks found in this department</p>
            </div>
          )}
        </div>
      </div>

      {/* Role Management Modal */}
      {showRoleModal && (
        <RoleManagementModal
          groupKey={groupKey || ''}
          groupName={groupInfo?.group_name || ''}
          isOpen={showRoleModal}
          onClose={() => setShowRoleModal(false)}
        />
      )}
    </div>
  );
}

// Role Management Modal Component
interface RoleManagementModalProps {
  groupKey: string;
  groupName: string;
  isOpen: boolean;
  onClose: () => void;
}

function RoleManagementModal({ groupKey, groupName, isOpen, onClose }: RoleManagementModalProps) {
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchRoleData();
    }
  }, [isOpen, groupKey]);

  const fetchRoleData = async () => {
    setLoading(true);
    try {
      // Fetch available roles and existing group role mapping in parallel
      const [availableRolesData, groupRoleData] = await Promise.all([
        groupRoleService.getAllRoles(),
        groupRoleService.getGroupRoleByGroupKey(groupKey)
      ]);

      setAvailableRoles(availableRolesData);

      // Set selected roles based on existing mapping
      if (groupRoleData?.roles) {
        setSelectedRoles(groupRoleData.roles.map(role => role.role_key));
      } else {
        setSelectedRoles([]);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching role data:', error);
      setLoading(false);
      toast.error('Failed to load role data');
    }
  };

  const handleSaveRoles = async () => {
    setSaving(true);
    try {
      // Convert selected role keys to full role objects
      const selectedRoleObjects = availableRoles.filter(role =>
        selectedRoles.includes(role.role_key)
      );

      // Save the roles using the new upsert service
      await groupRoleService.upsertGroupRoles(groupKey, selectedRoleObjects);

      setSaving(false);
      toast.success('Roles updated successfully');
      onClose();
    } catch (error) {
      console.error('Error saving roles:', error);
      setSaving(false);
      toast.error('Failed to save roles');
    }
  };

  const toggleRole = (roleKey: string) => {
    setSelectedRoles(prev =>
      prev.includes(roleKey)
        ? prev.filter(r => r !== roleKey)
        : [...prev, roleKey]
    );
  };

  // Filter roles based on search query
  const filteredRoles = availableRoles.filter(role =>
    role.role_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    role.role_key.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Role Management</h2>
            <p className="text-sm text-gray-600 mt-1">Manage roles for {groupName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XCircle className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                  <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <p className="text-gray-600">Loading roles...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search roles by name or key..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Available Roles {searchQuery && `(${filteredRoles.length} of ${availableRoles.length})`}
                </h3>
                <div className="space-y-2">
                  {filteredRoles.length === 0 && searchQuery ? (
                    <div className="text-center py-8">
                      <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No roles found matching "{searchQuery}"</p>
                    </div>
                  ) : (
                    filteredRoles.map((role) => (
                      <label
                        key={role.role_key}
                        className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={selectedRoles.includes(role.role_key)}
                          onChange={() => toggleRole(role.role_key)}
                          className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {role.role_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            Role Key: {role.role_key}
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0">
          <div className="text-sm text-gray-500">
            {selectedRoles.length} role(s) selected
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveRoles}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}